/**
 * Saying something at the table.
 *
 * Two ways to say it, and they are not the same thing.
 *
 * A **phrase** is an id. The client sends the id, the server maps it back to
 * words from its own config, and so the list here is presentation while the
 * server's is the authority. Nothing a client invents ever becomes a phrase.
 *
 * **Typed words** are a player's own, and they are limited rather than
 * vocabulary-checked: tidied and measured by the protocol, rate-limited by the
 * server, and switchable off by the host. This file used to explain that there
 * was no text box and therefore no moderation problem to solve; that is no
 * longer true, and the honest version is that the limits are what they are and
 * the host switch is the real remedy.
 *
 * Both are recorded in one log, so there is a single idea of "what was said"
 * rather than two — and so the log still works at a table where typing is off.
 */

import { useEffect, useRef, useState } from 'react';
import { Limits, normaliseChatText } from '@cards/protocol';
import type { ChatEntry } from '../net/match.js';
import type { ChatPhrase } from '../net/session.js';
import type { SeatInfo } from '@cards/protocol';
import { avatarEmoji } from '../render/avatars.js';
import { seatColour } from '../render/seat-colour.js';

/** What a seat is called, or a sensible stand-in if they have left. */
function nameOf(seats: readonly SeatInfo[], seat: number): string {
  return seats.find((info) => info.seat === seat)?.displayName ?? `Seat ${String(seat + 1)}`;
}

/**
 * The face that seat picked (I8).
 *
 * Falls back to a neutral mark rather than nothing: a line whose avatar is
 * missing should still line up with the ones around it, and somebody who has
 * left the table is exactly when you are most likely to be reading back.
 */
function faceOfSeat(seats: readonly SeatInfo[], seat: number): string {
  const info = seats.find((entry) => entry.seat === seat);
  return info ? avatarEmoji(info.avatarId) : '·';
}

/**
 * What one entry says.
 *
 * A phrase is looked up in the same list the buttons come from, so the wording
 * on screen is the wording the server chose. An id with no phrase behind it any
 * more — the list changed under a long match — reads as the id rather than as
 * nothing, because a blank line is a worse answer than an ugly one.
 */
function wordsOf(entry: ChatEntry, phrases: readonly ChatPhrase[]): string {
  if (entry.text !== undefined) return entry.text;
  if (entry.phraseId === undefined) return '';
  return phrases.find((phrase) => phrase.id === entry.phraseId)?.text ?? entry.phraseId;
}

export function ChatSheet({
  phrases,
  log,
  seats,
  allowTextChat,
  onSay,
  onType,
  onClose,
}: {
  phrases: readonly ChatPhrase[];
  log: readonly ChatEntry[];
  seats: readonly SeatInfo[];
  allowTextChat: boolean;
  onSay: (phraseId: string) => void;
  onType: (text: string) => void;
  onClose: () => void;
}) {
  const firstRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    // The input if there is one, because somebody opening this to type should
    // not have to tap again. The first phrase otherwise.
    if (allowTextChat) inputRef.current?.focus();
    else firstRef.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose, allowTextChat]);

  // Newest at the bottom, and the bottom is where you want to be looking.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [log.length]);

  /*
   * Tidied the same way the server will tidy it, so the count agrees.
   *
   * `normaliseChatText` is exported from the protocol for exactly this: a
   * client that measured what was typed rather than what will be shown would
   * disagree with the server about whether a message fits.
   */
  const ready = normaliseChatText(draft);
  const tooLong = ready.length > Limits.CHAT_TEXT;
  const canSend = ready.length > 0 && !tooLong;

  const send = () => {
    if (!canSend) return;
    onType(ready);
    setDraft('');
    onClose();
  };

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label="Say something">
      <div className="sheet-body">
        <p className="sheet-title">Say something</p>

        <div className="chat-log" role="log" aria-label="What has been said">
          {log.length === 0 ? (
            <p className="chat-empty">Nothing said yet.</p>
          ) : (
            log.map((entry) => (
              /*
                Avatar, name, words (I8) - keyed to that seat's colour (H19).

                The colour is on the rule down the left and on nothing else: it
                separates one speaker's lines from the next at a glance without
                being the thing you have to read. The face and the name are
                still there, so the line works with no colour at all.
              */
              <p
                className="chat-line"
                key={entry.n}
                style={{ '--who': seatColour(entry.seat) } as React.CSSProperties}
              >
                <span className="chat-face" aria-hidden="true">
                  {faceOfSeat(seats, entry.seat)}
                </span>
                <span className="chat-who">{nameOf(seats, entry.seat)}</span>
                <span className="chat-said">{wordsOf(entry, phrases)}</span>
              </p>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        <div className="chat-phrases">
          {phrases.map((phrase, index) => (
            <button
              key={phrase.id}
              ref={index === 0 ? firstRef : undefined}
              type="button"
              className="chat-phrase"
              onClick={() => {
                onSay(phrase.id);
                // Said is said: holding the sheet open over the table hides the
                // bubble that is the whole point of having pressed it.
                onClose();
              }}
            >
              {phrase.text}
            </button>
          ))}
        </div>

        {/*
          Absent rather than disabled when the host has typing off.
          A box you cannot type in is a worse explanation than no box, and the
          phrase buttons above it still work — which is the thing to notice.
        */}
        {allowTextChat ? (
          <form
            className="chat-compose"
            onSubmit={(event) => {
              event.preventDefault();
              send();
            }}
          >
            <input
              ref={inputRef}
              className="chat-input"
              type="text"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Type a message"
              aria-label="Type a message"
              // Generous, because the real limit is applied to the tidied text
              // and the counter below says what it will actually be.
              maxLength={Limits.CHAT_TEXT * 2}
              autoComplete="off"
            />
            <button type="submit" className="chat-send" disabled={!canSend}>
              Send
            </button>
          </form>
        ) : null}

        {allowTextChat && ready.length > Limits.CHAT_TEXT - 20 ? (
          <p className={tooLong ? 'chat-count over' : 'chat-count'}>
            {`${String(ready.length)} / ${String(Limits.CHAT_TEXT)}`}
          </p>
        ) : null}

        <button type="button" className="text-button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  );
}
